/**
 * Polling, for one document and for a batch.
 *
 * Both honour the same contract the plugin's tasks do: **cancelling stops the
 * waiting, not the extraction.** The server was paid for the document the
 * moment the worker billed it, so a cancelled poll leaves a job that is still
 * collectable rather than throwing the work away. The UI says so.
 *
 * Intervals match the plugin: three seconds for a single document, because a
 * fast deed finishes in fifteen and a longer poll would be most of the wait;
 * twenty for a batch, which settles in tens of minutes and would otherwise
 * spend hours of polling on a result that arrives once.
 */

import type { Api } from "./api";
import { ApiError } from "./api";
import type { BatchStatus, ExtractJob, HeldResponse } from "./types";

export const EXTRACT_POLL_MS = 3_000;
export const BATCH_POLL_MS = 20_000;
/** Past this, something is wrong upstream and a spinner is a lie. Matches the
 *  plugin's own ceiling, which is the Lambda's 900 seconds plus slack. */
export const EXTRACT_TIMEOUT_MS = 1_000_000;

export interface Progress {
  (message: string, elapsedSeconds: number): void;
}

export class Cancelled extends Error {
  constructor() {
    super(
      "Stopped waiting. The extraction is still running and stays collectable.",
    );
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Submit one document and wait for it.
 *
 * Documents past the direct ceiling are staged first, which is the same
 * decision `hosted.submit_extract` makes at 4.5 MB; the browser has the same
 * ceiling because the same Function URL is on the other end.
 */
export async function runExtract(
  api: Api,
  file: File,
  signal: AbortSignal,
  onProgress: Progress,
): Promise<{ job: ExtractJob; held: HeldResponse }> {
  const started = Date.now();
  const elapsed = () => Math.round((Date.now() - started) / 1000);

  let uploadId: string | undefined;
  if (file.size > DIRECT_LIMIT_BYTES) {
    onProgress("Uploading…", elapsed());
    uploadId = await api.stage(file);
  }
  onProgress("Submitting…", elapsed());
  const submitted = await api.submit(file, uploadId);
  return awaitExtract(api, submitted.job, started, signal, onProgress);
}

/**
 * Submit a pasted legal description and wait for it.
 *
 * No staging branch: text is thousands of times under the direct ceiling.
 * Everything after the submit is `runExtract`'s own wait.
 */
export async function runExtractText(
  api: Api,
  text: string,
  docName: string,
  signal: AbortSignal,
  onProgress: Progress,
): Promise<{ job: ExtractJob; held: HeldResponse }> {
  const started = Date.now();
  onProgress("Submitting…", 0);
  const submitted = await api.submitText(text, docName);
  return awaitExtract(api, submitted.job, started, signal, onProgress);
}

async function awaitExtract(
  api: Api,
  jobId: string,
  started: number,
  signal: AbortSignal,
  onProgress: Progress,
): Promise<{ job: ExtractJob; held: HeldResponse }> {
  const elapsed = () => Math.round((Date.now() - started) / 1000);
  onProgress("Extracting…", elapsed());
  while (Date.now() - started < EXTRACT_TIMEOUT_MS) {
    if (signal.aborted) throw new Cancelled();
    await sleep(EXTRACT_POLL_MS);
    if (signal.aborted) throw new Cancelled();
    const { job, held } = await api.pollExtract(jobId);
    if (job.status === "succeeded" && held) return { job, held };
    if (job.status === "failed") {
      throw new ApiError(
        502,
        "extraction_failed",
        job.error || "The extraction failed.",
      );
    }
    onProgress(job.status === "running" ? "Extracting…" : "Queued…", elapsed());
  }
  throw new ApiError(
    504,
    "timed_out",
    "This document is taking longer than expected.",
  );
}

/** The Function URL base64-encodes bodies, so a direct POST caps near 4.5 MB
 *  and anything bigger goes through the staging bucket. */
export const DIRECT_LIMIT_BYTES = 4_500_000;

export async function runBatch(
  api: Api,
  files: File[],
  signal: AbortSignal,
  onProgress: (status: BatchStatus, elapsedSeconds: number) => void,
): Promise<HeldResponse[]> {
  const started = Date.now();
  const elapsed = () => Math.round((Date.now() - started) / 1000);
  const submitted = await api.submitBatch(files);
  onProgress(submitted, elapsed());

  for (;;) {
    if (signal.aborted) throw new Cancelled();
    await sleep(BATCH_POLL_MS);
    if (signal.aborted) throw new Cancelled();
    const status = await api.batch(submitted.batch);
    onProgress(status, elapsed());
    if (TERMINAL.has(status.status)) {
      // Collecting starts no clock: results stay on the server until
      // somebody deletes them, so this can be fetched again if the tab dies.
      return api.batchResults(submitted.batch);
    }
  }
}

const TERMINAL = new Set([
  "ended",
  "completed",
  "failed",
  "cancelled",
  "expired",
]);
